import clsx from "clsx";
import { CircleHelp, TriangleAlert } from "lucide-react";
import { createPortal } from "react-dom";
import { createContext, type ReactNode, useCallback, useContext, useEffect, useId, useRef, useState } from "react";

/*
 * Asking before doing something (the standard for every confirmation): a small card saying what
 * will happen, with the action's own button and Cancel, over what it's about, dimmed behind it.
 * Nothing behind it moves. Escape or Cancel says no.
 *
 *   const confirm = useConfirm();
 *   if (await confirm({ title: "Delete this group?", message: "...", confirmLabel: "Delete group", tone: "danger" })) remove();
 *
 * Asked from inside a window (RecordWindow), it covers that window only, so it's clear which record
 * it's about and the rest of the page stays as it is; from a page, it covers the page
 * (ConfirmProvider in main.tsx). The nearest one asks: a window's own wraps its contents.
 * LeaveGuard uses the same card.
 */

export interface ConfirmOptions {
    /** The question ("Take it out of the Deadbolts group?"). */
    title: string;
    /** What it's about, on its own under the question (an item's name), so it doesn't run into the sentence. */
    subject?: string;
    /** What happens, in a sentence or two; or more (e.g. a list of what changes). With `detail` or `hold`, the one main line (dark). */
    message?: ReactNode;
    /**
     * More about it, small under the message: what it means, why ("On 5 active jobs: ..."). The
     * standard for a held question (`hold`): the change as the message, the rest here.
     */
    detail?: ReactNode;
    /** The detail is a warning (amber), not just more to know (grey). */
    detailTone?: "warning";
    /** The action, as its button reads ("Take out", "Delete group"): never "OK" or "Yes". */
    confirmLabel: string;
    cancelLabel?: string;
    /**
     * "danger": it removes something or can't easily be undone (a red button, a warning sign, and
     * Cancel has the focus, so Enter by accident does nothing); "primary" (the default): an ordinary
     * action (a green button with the focus).
     */
    tone?: "danger" | "primary";
    /**
     * The action is pressed and held (about a second, its button filling as it's held) instead of
     * clicked: for a change that mustn't happen by a reflex click or Enter, e.g. a locked price.
     */
    hold?: boolean;
}

type Answers = { onConfirm: () => void; onCancel: () => void };

/**
 * The card, all on white: a sign (red for a danger, green otherwise, in a soft circle) beside the
 * question; under the question, what it's about (dark) and what happens (grey); then Cancel and
 * the action (solid: the decisive button), bottom right. The safe button has the focus for a danger.
 */
function ConfirmCard(props: Readonly<ConfirmOptions & Answers & { titleId: string }>) {
    const danger = props.tone === "danger";
    const Icon = danger ? TriangleAlert : CircleHelp;
    return (
        <div className="p-5">
            <div className="flex items-center gap-3">
                <span className={danger ? "flex size-8 shrink-0 items-center justify-center rounded-full bg-danger-soft text-danger" : "flex size-8 shrink-0 items-center justify-center rounded-full bg-brand-500/10 text-brand-600"}>
                    <Icon className="size-4" />
                </span>
                <h2 id={props.titleId} className="min-w-0 text-[15px] leading-snug font-semibold">
                    {props.title}
                </h2>
            </div>
            {/* In line with the question, past the sign (its 2rem and the 0.75rem gap). */}
            {props.subject || props.message || props.detail ? (
                <div className="mt-1.5 space-y-1 pl-11">
                    {props.subject ? (
                        <p className="m-0 truncate font-medium text-ink" title={props.subject}>
                            {props.subject}
                        </p>
                    ) : null}
                    {props.message ? <div className={clsx("max-h-[50vh] overflow-y-auto", props.detail || props.hold ? "text-ink" : "text-muted")}>{props.message}</div> : null}
                    {props.detail ? <p className={clsx("m-0 text-xs", props.detailTone === "warning" ? "text-warning" : "text-muted")}>{props.detail}</p> : null}
                </div>
            ) : null}
            {/* The buttons share a height: Cancel matches a held action's two lines, its words centred. */}
            <div className="mt-5 flex items-stretch justify-end gap-2">
                {/* The safe answer first: Cancel when it removes something, else the action (data-first: ConfirmDialog focuses it once open). */}
                <button type="button" className="btn-secondary" autoFocus={danger} data-first={danger || undefined} onClick={props.onCancel}>
                    {props.cancelLabel ?? "Cancel"}
                </button>
                {props.hold ? (
                    <HoldButton className={danger ? "btn-danger-solid" : "btn-primary"} label={props.confirmLabel} focus={!danger} onDone={props.onConfirm} />
                ) : (
                    <button type="button" className={danger ? "btn-danger-solid" : "btn-primary"} autoFocus={!danger} data-first={!danger || undefined} onClick={props.onConfirm}>
                        {props.confirmLabel}
                    </button>
                )}
            </div>
        </div>
    );
}

/** How long the action is held to confirm it (ConfirmOptions.hold). */
const HOLD_MS = 1000;

/**
 * An action that's pressed and held to confirm (mouse, touch, or Enter / Space held down): it fills
 * from the left as it's held, and goes ahead when full. Let go early and nothing happens; a click
 * alone does nothing either ("Hold to confirm", small under its label, says so). `focus`: it has the
 * focus when asked (a quick Enter still does nothing).
 */
function HoldButton(props: Readonly<{ className: string; label: string; focus: boolean; onDone: () => void }>) {
    const [progress, setProgress] = useState(0);
    const frame = useRef<number | null>(null);
    const started = useRef(0);
    const stop = useCallback(() => {
        if (frame.current !== null) cancelAnimationFrame(frame.current);
        frame.current = null;
        setProgress(0);
    }, []);
    useEffect(() => stop, [stop]);
    const begin = () => {
        if (frame.current !== null) return;
        started.current = performance.now();
        const tick = (now: number) => {
            const p = Math.min((now - started.current) / HOLD_MS, 1);
            setProgress(p);
            if (p >= 1) {
                frame.current = null;
                props.onDone();
                return;
            }
            frame.current = requestAnimationFrame(tick);
        };
        frame.current = requestAnimationFrame(tick);
    };
    const isHoldKey = (key: string) => key === "Enter" || key === " ";
    return (
        <button
            type="button"
            className={`${props.className} relative flex-col gap-0 overflow-hidden py-1.5 leading-tight select-none`}
            autoFocus={props.focus}
            data-first={props.focus || undefined}
            aria-description={`Hold for a second to ${props.label.toLowerCase()}`}
            onPointerDown={(e) => {
                e.preventDefault();
                begin();
            }}
            onPointerUp={stop}
            onPointerLeave={stop}
            onPointerCancel={stop}
            onKeyDown={(e) => {
                if (!isHoldKey(e.key)) return;
                e.preventDefault();
                if (!e.repeat) begin();
            }}
            onKeyUp={(e) => isHoldKey(e.key) && stop()}
            onBlur={stop}
            // Touch: a long press mustn't open the browser's menu.
            onContextMenu={(e) => e.preventDefault()}
        >
            <span aria-hidden className="absolute inset-y-0 left-0 bg-white/30" style={{ width: `${progress * 100}%` }} />
            {/* The same words throughout, so it doesn't change size as it's held. */}
            <span className="relative">{props.label}</span>
            <span className="relative text-[11px] font-normal opacity-85">Hold to confirm</span>
        </button>
    );
}

const CARD = "confirm-card w-[min(28rem,calc(100%-2rem))] overflow-hidden rounded-xl border border-line bg-surface text-sm text-ink shadow-2xl";

/** Over the page, open while \`open\`: for a page's confirmations, or a flow that asks its own way (LeaveGuard). */
export function ConfirmDialog(props: Readonly<ConfirmOptions & Answers & { open: boolean }>) {
    const dialog = useRef<HTMLDialogElement>(null);
    const titleId = useId();
    useEffect(() => {
        const d = dialog.current;
        if (!d) return;
        if (props.open && !d.open) {
            d.showModal();
            // Its card mounted before it opened, so autoFocus came too early (showModal then picks the first button): again now.
            d.querySelector<HTMLElement>("[data-first]")?.focus();
        }
        if (!props.open && d.open) d.close();
    }, [props.open]);
    return (
        <dialog
            ref={dialog}
            aria-labelledby={titleId}
            className={`m-auto p-0 backdrop:bg-black/30 ${CARD}`}
            onCancel={(e) => {
                // Escape: no (closed by \`open\` going false, as every other way).
                e.preventDefault();
                props.onCancel();
            }}
        >
            {props.open ? <ConfirmCard {...props} titleId={titleId} /> : null}
        </dialog>
    );
}

/** Over a window only (the window dimmed behind it): what the window's own confirmations show. */
function WindowConfirm(props: Readonly<ConfirmOptions & Answers>) {
    const titleId = useId();
    useEscape(props.onCancel);
    return (
        <div
            className="confirm-shade absolute inset-0 z-30 flex items-center justify-center bg-black/30 p-4"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby={titleId}
            // A click on the shade keeps the focus in the card (it's no answer: one of the buttons is).
            onMouseDown={(e) => e.target === e.currentTarget && e.preventDefault()}
        >
            <div className={CARD}>
                <ConfirmCard {...props} titleId={titleId} />
            </div>
        </div>
    );
}

/** Over its window's body (WindowLayer), not its title bar: as its popups are. */
function InWindow({ children }: Readonly<{ children: ReactNode }>) {
    const layer = useContext(WindowLayer);
    return layer ? createPortal(children, layer) : children;
}

/** What's open over windows, newest last: Escape answers only the newest (a question over a popup, not both). */
const escapeStack: { current: () => void }[] = [];

/**
 * While something is open over a window (a question, a popup): Escape closes it, wherever the
 * focus is, and only it. Caught first (capture), so the window's own Escape (closing it) never sees it.
 */
export function useEscape(onEscape: () => void) {
    const latest = useRef(onEscape);
    latest.current = onEscape;
    useEffect(() => {
        escapeStack.push(latest);
        const onKey = (e: KeyboardEvent) => {
            if (e.key !== "Escape" || escapeStack.at(-1) !== latest) return;
            // A question over the whole page (ConfirmDialog, LeaveGuard) is above every window: it answers Escape itself.
            if (document.querySelector("dialog:modal")) return;
            e.preventDefault();
            e.stopPropagation();
            latest.current();
        };
        document.addEventListener("keydown", onKey, true);
        return () => {
            escapeStack.splice(escapeStack.indexOf(latest), 1);
            document.removeEventListener("keydown", onKey, true);
        };
    }, []);
}

/**
 * The window it's in: its body under its title bar (RecordWindow), for its popups and questions to cover, so its title bar still
 * moves, expands and closes it. Null outside a window.
 */
export const WindowLayer = createContext<HTMLElement | null>(null);

type Confirm = (options: ConfirmOptions) => Promise<boolean>;
const ConfirmContext = createContext<Confirm | null>(null);

/**
 * Where confirmations are asked: the page (main.tsx), or \`inWindow\` a window, covering only it
 * (RecordWindow wraps its contents in one, inside its positioned frame). The nearest one asks.
 */
export function ConfirmProvider({ inWindow = false, children }: Readonly<{ inWindow?: boolean; children: ReactNode }>) {
    const [asking, setAsking] = useState<{ options: ConfirmOptions; answer: (yes: boolean) => void } | null>(null);
    const confirm = useCallback<Confirm>(
        (options) =>
            new Promise((answer) => {
                // A new question replaces one still open (that one's a no).
                setAsking((current) => {
                    current?.answer(false);
                    return { options, answer };
                });
            }),
        [],
    );
    const answers = {
        onConfirm: () => {
            asking?.answer(true);
            setAsking(null);
        },
        onCancel: () => {
            asking?.answer(false);
            setAsking(null);
        },
    };
    let shown: ReactNode = null;
    if (inWindow) shown = asking ? <InWindow><WindowConfirm {...asking.options} {...answers} /></InWindow> : null;
    else shown = <ConfirmDialog {...(asking?.options ?? { title: "", confirmLabel: "" })} {...answers} open={asking !== null} />;
    return (
        <ConfirmContext.Provider value={confirm}>
            {children}
            {shown}
        </ConfirmContext.Provider>
    );
}

/** Ask before doing something: resolves true when they choose the action, false for Cancel or Escape. */
export function useConfirm(): Confirm {
    const confirm = useContext(ConfirmContext);
    if (!confirm) throw new Error("useConfirm needs a ConfirmProvider (main.tsx, or a RecordWindow)");
    return confirm;
}

/**
 * For a window's own component, which draws its RecordWindow (so is outside it): ask in that window, covering only it, not the
 * page. Its `Here` goes inside the window (e.g. in its notices) and hands the window's confirm back; before it's there, the page's.
 */
export function useWindowConfirm(): { confirm: Confirm; Here: () => null } {
    const page = useConfirm();
    const windowConfirm = useRef<Confirm | null>(null);
    // One component for the window's life (a new one each render would remount it).
    const [Here] = useState(() => {
        function Here() {
            windowConfirm.current = useConfirm();
            return null;
        }
        return Here;
    });
    return { confirm: (options) => (windowConfirm.current ?? page)(options), Here };
}
